import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment-timezone";
import LocalStorageUtil from "./LocalStorageUtil";
import { LocalStorageConstants } from "../constants/ApplicationConstants/LocalStorageConstants";
import { UserInterface } from "../shared/interface/auth/User.interface";

export class ExportFile {
  public static excelFileDownload = (data: any[], moduleName: string) => {
    try {
      // Create a new workbook
      const workbook = XLSX.utils.book_new();

      // Convert the array of objects to a worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);

      // Dynamically adjust column widths based on the length of the data
      const columnWidths = Object.keys(data[0]).map((key) => {
        const maxLength = data.reduce((max, row) => {
          return row[key].toString().length > max ? row[key].toString().length : max;
        }, key.length); // Compare with header length as well
        return { wch: maxLength + 2 }; // Add some extra space for padding
      });

      worksheet["!cols"] = columnWidths;

      // Append the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

      // Generate the Excel file and trigger the download
      XLSX.writeFile(workbook, `${moduleName}_${moment().format("DD-MM-YYYY HH:mm")}.xlsx`);
    } catch (error) {
      throw error;
    }
  };

  // public static pdfFileDownload = (data: any[], moduleName: string) => {
  //   try {
  //     const doc: any = new jsPDF("p", "mm", "a4"); // Create a new PDF document with A4 size

  //     const pageHeight = doc.internal.pageSize.getHeight(); // Get A4 page height

  //     // Extract headers from the data keys
  //     const headers = [Object.keys(data[0])]; // This will give an array of arrays as expected by autoTable

  //     // Prepare rows for the PDF table
  //     const rows = data.map((row) => Object.keys(row).map((key) => row[key].toString()));

  //     // Adjust the font size based on content and page size
  //     let dynamicFontSize = 6;

  //     // Add title to the PDF
  //     doc.text(moduleName, 14, 10);

  //     // Add autoTable with headers, rows, and fixed column widths to enable wrapping
  //     autoTable(doc, {
  //       head: headers,
  //       body: rows,
  //       startY: 20,
  //       styles: { fontSize: dynamicFontSize, cellPadding: 2, overflow: "linebreak" }, // Enable line breaks
  //       theme: "grid", // Table theme: 'grid', 'striped', or 'plain'
  //       headStyles: { fillColor: [0, 102, 204] }, // Styling for headers
  //       margin: { top: 20, left: 10, right: 10 }, // Adjust margins to fit A4 page
  //       pageBreak: "auto" // Automatically break pages if content overflows
  //     });

  //     // Ensure the table fits within the A4 page dimensions
  //     const totalHeight = doc.previousAutoTable?.finalY + 10; // Get the final position of the table

  //     if (totalHeight > pageHeight) {
  //       doc.addPage(); // Add a new page if content exceeds page height
  //     }

  //     // Save the generated PDF
  //     doc.save(`${moduleName}_${moment().format("DD-MM-YYYY HH:mm")}.pdf`);
  //   } catch (error) {
  //     throw error;
  //   }
  // };

  private static async loadImage(imagePath: string): Promise<string | null> {
    try {
      const baseURL = process.env.REACT_APP_BASE_IMAGE_PATH;
      const logoImagePath = `${baseURL}/${imagePath}`;

      const request = new Request(logoImagePath, {
        credentials: "include",
        method: "GET",
        headers: { Accept: "application/json" }
      });

      const response = await fetch(request);
      const blob = await response.blob();
      const reader = new FileReader();

      return new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(blob); // Convert Blob to base64 string
      });
    } catch (error) {
      console.error("Error loading image:", error);
      return null;
    }
  }

  public static pdfFileDownload = async (
    data: any[],
    moduleName: string,
    headerInfo?:
      | {
          header?: string;
          customerName?: string;
          locationName?: string;
          requestType?: string;
          fromDate: string | undefined;
          toDate: string | undefined;
          clientName?: string;
          keyFobToken?: string | number;
          requestAckStatus?: string;
          selectedCompletionStatus?: string;
        }
      | any
  ) => {
    try {
      const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

      // Create a new PDF document with A4 size
      const doc: any = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true
      });

      let currentY = 10;
      const pageWidth = doc.internal.pageSize.getWidth(); // Get the page width

      // Add header content
      doc.setFontSize(14);

      // Logo image
      const imagePath = userData?.config?.theme?.LOGO_IMAGE_PATH ?? "";

      if (imagePath) {
        // Set the header text on the left side
        const headerText = `${userData?.config?.theme?.HEADER_TEXT || ""}` || "parkdux";
        const logoWidth = 50; // Width of the logo
        const logoHeight = 20; // Height of the logo
        const logoMarginRight = 0; // Margin from the right edge for the logo
        const logoMarginTop = -10; // Set logo margin top to -10px (10px up)

        // Left margin for header text (you can adjust this if needed)
        const headerMarginLeft = 10; // 10 units from the left edge for the header text
        const currentY = 10; // Set the Y position for both the header text and logo

        // Add header text to the left side
        doc.text(headerText, headerMarginLeft, currentY);

        // Load logo image
        const logoImage = await this.loadImage(imagePath);

        // Calculate margin for the right edge (top-right corner) for the logo
        const marginLeftForLogo = pageWidth - logoWidth - logoMarginRight;

        // Add the logo image to the top-right corner with adjusted margin top
        doc.addImage(logoImage, "JPEG", marginLeftForLogo, currentY + logoMarginTop, logoWidth, logoHeight);
      }

      currentY += 10;
      doc.setFontSize(10);

      const firstTableData = [
        [{ content: "Customer Name" }, headerInfo?.customerName || "", { content: "Location Name" }, headerInfo?.locationName || ""],
        [{ content: "Report Name" }, moduleName || "", { content: "Generated at" }, moment().format("DD-MM-YYYY HH:mm")]
      ];

      autoTable(doc, {
        body: firstTableData,
        startY: currentY,
        theme: "grid",
        styles: {
          fontSize: 10,
          cellPadding: 2,
          overflow: "linebreak"
        },
        columnStyles: {
          0: {
            cellWidth: 45,
            fillColor: [220, 220, 220]
          },
          1: { cellWidth: 50 },
          2: {
            cellWidth: 45,
            fillColor: [220, 220, 220]
          },
          3: { cellWidth: 50 }
        },
        didDrawCell: (data) => {
          if (data.row.index === 0 || data.row.index === 1) {
            doc.setFont("helvetica", "bold");
          }
        },
        margin: { top: 20, left: 10, right: 10 }
      });
      currentY = doc.previousAutoTable.finalY + 5;

      // Check if filter data is available before rendering the table
      const filterTableData = [
        ...(headerInfo?.requestType
          ? [
              [
                { content: "Request Type", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.requestType ?? "", styles: { fontStyle: "normal" as const } },
                { content: "Key Fob Token", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.keyFobToken || "-", styles: { fontStyle: "normal" as const } }
              ]
            ]
          : []),

        ...(!headerInfo?.requestType && (headerInfo?.clientName || headerInfo.keyFobToken)
          ? [
              [
                { content: "Client Name", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.clientName || "-", styles: { fontStyle: "normal" as const } },
                { content: "Key Fob Token", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.keyFobToken || "-", styles: { fontStyle: "normal" as const } }
              ]
            ]
          : []),

        ...(headerInfo?.requestType && headerInfo?.clientName
          ? [
              [
                { content: "Client Name", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.clientName || "-", colSpan: 3, styles: { fontStyle: "normal" as const } }
              ]
            ]
          : []),

        ...(headerInfo?.selectedCompletionStatus || headerInfo?.requestAckStatus
          ? [
              [
                { content: "Completion Status", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.selectedCompletionStatus || "-", styles: { fontStyle: "normal" as const } },
                { content: "Request ACK Status", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.requestAckStatus || "-", styles: { fontStyle: "normal" as const } }
              ]
            ]
          : []),

        ...(headerInfo?.fromDate || headerInfo?.toDate
          ? [
              [
                { content: "From Date", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.fromDate || "-", styles: { fontStyle: "normal" as const } },
                { content: "To Date", styles: { fontStyle: "normal" as const } },
                { content: headerInfo.toDate || "-", styles: { fontStyle: "normal" as const } }
              ]
            ]
          : [])
      ];

      // **Dynamically add other fields if available**
      const dynamicFields: { label: string; key: keyof any }[] = [
        // { label: "Assigned User", key: "assignedUser" }
      ];

      dynamicFields.forEach(({ label, key }) => {
        if (headerInfo?.[key])
          filterTableData.push([
            { content: `${label}:`, styles: { fontStyle: "normal" } },
            { content: headerInfo?.[key] ?? "-", colSpan: 3, styles: { fontStyle: "normal" } }
          ]);
      });

      autoTable(doc, {
        body: filterTableData,
        startY: doc.previousAutoTable.finalY + 5,
        theme: "grid",
        styles: {
          fontSize: 10,
          cellPadding: 2,
          overflow: "linebreak"
        },
        columnStyles: {
          0: {
            cellWidth: 45,
            fillColor: [220, 220, 220]
          },
          1: { cellWidth: 50 },
          2: {
            cellWidth: 45,
            fillColor: [220, 220, 220]
          },
          3: { cellWidth: 50 }
        },
        margin: { top: 20, left: 10, right: 10 }
      });

      // Update currentY after the table
      currentY = doc.previousAutoTable.finalY + 5;

      const headers = [Object.keys(data[0])];
      const rows = data.map((row) => Object.keys(row).map((key) => (row[key] !== null && row[key] !== undefined ? row[key].toString() : "")));

      const PRIMARY_COLOR = headerInfo?.PRIMARY_COLOR || userData?.config?.theme?.PRIMARY_COLOR;

      autoTable(doc, {
        head: headers,
        body: rows,
        startY: currentY + 1,
        styles: { fontSize: 6, cellPadding: 2, overflow: "linebreak" },
        theme: "grid",
        headStyles: { fillColor: PRIMARY_COLOR },
        margin: { top: 20, left: 10, right: 10 },
        pageBreak: "auto"
      });

      doc.save(`${moduleName}_${moment().format("DD-MM-YYYY HH:mm")}.pdf`);
    } catch (error) {
      throw error;
    }
  };
}
